import React, { createContext, useState } from "react";
import {
  useMediaQuery,
  WhmAvatar,
  WhmBox,
  WhmButton,
  WhmCheckbox,
  WhmDialog,
  WhmDialogActions,
  WhmDialogContent,
  WhmDialogTitle,
  WhmFormControlLabel,
  WhmStack,
  whmTheme,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { ReactComponent as ArrowRight } from "@securitize/assets-sec-ui/dist/icons/svg/arrow-right.svg";
import { ReactComponent as Securitize } from "@securitize/assets-sec-ui/dist/logos/svg/sec-logo-positive.svg";
import { Translate, TranslateHtml } from "../components/Translation/Translate";
import { translationKeys } from "./I18nContext";
import useUserInfo from "../hooks/useUserInfo";

type RedirectAuthenticatedContextType = {
  onRedirect: (params: {
    backUrl: string;
    issuer: { name: string; logo: string };
  }) => void;
};
export const RedirectAuthenticatedContext =
  createContext<RedirectAuthenticatedContextType>(
    {} as RedirectAuthenticatedContextType,
  );

export const RedirectAuthenticatedProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [dontAsk, setDontAsk] = useState(false);
  const [redirect, setRedirect] = useState<{
    visible: boolean;
    backUrl?: string;
    issuer?: { name: string; logo: string };
  }>({ visible: false });
  const user = useUserInfo();
  const isMobile = useMediaQuery(whmTheme.breakpoints.down("xl"));

  const onRedirect = ({
    backUrl,
    issuer,
  }: {
    backUrl: string;
    issuer: { name: string; logo: string };
  }) => {
    const redirectWarningDisabledUsers = JSON.parse(
      localStorage.getItem("dontAskRedirectToIssuer") ?? "[]",
    ) as string[];

    if (redirectWarningDisabledUsers.includes(user.email)) {
      window.location.href = redirect.backUrl!;
      return;
    }

    setRedirect({ visible: true, backUrl, issuer });
  };

  return (
    <RedirectAuthenticatedContext.Provider
      value={{
        onRedirect,
      }}
    >
      {children}

      <WhmDialog
        open={redirect.visible}
        onClose={() => setRedirect((data) => ({ ...data, visible: false }))}
        fullScreen={isMobile}
        maxWidth={"xl"}
      >
        <WhmDialogTitle variant="h4">
          <Translate
            translationKey={translationKeys.REDIRECT_TO_ISSUER_TITLE}
            replacements={{
              issuer: redirect.issuer?.name ?? "",
            }}
          />
        </WhmDialogTitle>
        <WhmDialogContent
          sx={{
            ul: { marginTop: "16px" },
            li: { listStyleType: "disc", marginLeft: "1rem" },
          }}
        >
          <WhmStack gap="16px">
            <WhmStack
              direction="row"
              alignItems="center"
              alignSelf="center"
              sx={{ padding: "40px" }}
            >
              <WhmBox
                component={Securitize}
                viewBox="0 0 500 500"
                sx={{ height: "48px", width: "48px" }}
              />
              <WhmBox
                component={ArrowRight}
                sx={{ marginLeft: "4px", marginRight: "-8px", height: "19px" }}
              />
              <WhmBox
                component={ArrowRight}
                fill={whmTheme.palette.action.active}
                sx={{
                  marginLeft: "-8px",
                  marginRight: "4px",
                  height: "19px",
                  opacity: 0.5,
                }}
              />
              <WhmAvatar
                sx={{ height: "48px", width: "48px" }}
                src={redirect.issuer?.logo}
              />
            </WhmStack>
            <WhmTypography component="div">
              <TranslateHtml
                translationKey={translationKeys.REDIRECT_TO_ISSUER_BODY}
                replacements={{
                  issuer: redirect.issuer?.name ?? "",
                }}
              />
            </WhmTypography>
            <WhmFormControlLabel
              control={
                <WhmCheckbox
                  checked={dontAsk}
                  onChange={() => setDontAsk(!dontAsk)}
                />
              }
              label={
                <Translate
                  translationKey={
                    translationKeys.REDIRECT_TO_ISSUER_DO_NOT_ASK_AGAIN
                  }
                />
              }
            />
          </WhmStack>
        </WhmDialogContent>
        <WhmDialogActions>
          <WhmButton
            color="secondary"
            variant="outlined"
            onClick={() => setRedirect((data) => ({ ...data, visible: false }))}
          >
            <Translate
              translationKey={translationKeys.REDIRECT_TO_ISSUER_CANCEL}
            />
          </WhmButton>
          <WhmButton
            variant="contained"
            onClick={() => {
              if (dontAsk) {
                const redirectWarningDisabledUsers = JSON.parse(
                  localStorage.getItem("dontAskRedirectToIssuer") ?? "[]",
                ) as string[];
                redirectWarningDisabledUsers.push(user.email);
                localStorage.setItem(
                  "dontAskRedirectToIssuer",
                  JSON.stringify(redirectWarningDisabledUsers),
                );
              }

              // This redirect assumes that the Investor is approved in the Issuer.
              // It may be necessary to pass extra parameters in the future.
              window.location.href = redirect.backUrl!;
            }}
          >
            <Translate
              translationKey={translationKeys.REDIRECT_TO_ISSUER_CONTINUE}
              replacements={{
                issuer: redirect.issuer?.name ?? "",
              }}
            />
          </WhmButton>
        </WhmDialogActions>
      </WhmDialog>
    </RedirectAuthenticatedContext.Provider>
  );
};
